/**
 * Netflix-style horizontal browse row for scenario categories.
 * Each row has its own Embla carousel instance.
 *
 * Progressive loading: starts with INITIAL_BATCH cards, loads more
 * as the user scrolls near the end of the carousel.
 */

import { useCallback, useEffect, useState, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useBrand } from "@/config/brands";
import type { ViewportTier } from "@/hooks/useViewportTier";

const INITIAL_BATCH = 15;
const LOAD_MORE_BATCH = 15;

interface CategoryBrowseRowProps {
  label: string;
  color: string;
  count: number;
  scenarios: { id: string }[];
  renderCard: (scenario: any) => ReactNode;
  viewportTier: ViewportTier;
}

export function CategoryBrowseRow({
  label,
  color,
  count,
  scenarios,
  renderCard,
  viewportTier,
}: CategoryBrowseRowProps) {
  const { t } = useTranslation(['homepage']);
  const { isDark } = useTheme();
  const brand = useBrand();
  const ahpDark = brand.key === 'ahp' && isDark;

  const cardWidth = viewportTier === "ultrawide" ? 420 : viewportTier === "wide" ? 400 : 340;

  // Progressive loading: only render a batch at a time
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const visibleScenarios = useMemo(
    () => scenarios.slice(0, visibleCount),
    [scenarios, visibleCount],
  );
  const hasMore = visibleCount < scenarios.length;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
    slidesToScroll: 1,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());

    // Load more when user scrolls near the end
    const slideCount = emblaApi.slideNodes().length;
    const selectedIdx = emblaApi.selectedScrollSnap();
    // Embla snap count ≈ number of "pages" — load more when within last 3 snaps
    const snapCount = emblaApi.scrollSnapList().length;
    if (hasMore && selectedIdx >= snapCount - 3) {
      setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, scenarios.length));
    }
  }, [emblaApi, hasMore, scenarios.length]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-init Embla when visible count changes (new slides added)
  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [visibleCount, emblaApi]);

  if (scenarios.length < 2) return null;

  // Dark mode colors
  const headingColor = ahpDark ? "#FAF9F6" : "#1B1B1B";
  const subtextColor = ahpDark ? "rgba(250,249,246,0.45)" : "rgba(27,27,27,0.45)";
  const arrowBg = ahpDark ? "rgba(13,30,53,0.95)" : "rgba(255,255,255,0.95)";
  const arrowBorder = ahpDark ? "rgba(250,249,246,0.12)" : "rgba(27,27,27,0.12)";
  const arrowColor = ahpDark ? "#FAF9F6" : "#1B1B1B";
  const arrowShadow = ahpDark ? "0 2px 12px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.08)";

  return (
    <div className="group relative mb-10">
      {/* Header */}
      <div className="flex items-baseline gap-3 mb-4 px-12">
        <div
          className="w-1 h-6 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <h3
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: "clamp(20px, 2.5vw, 28px)",
            fontWeight: 400,
            color: headingColor,
            letterSpacing: "-0.3px",
          }}
        >
          {label}
        </h3>
        <span
          style={{
            fontSize: 13,
            color: subtextColor,
            fontFamily: "'Instrument Sans', sans-serif",
          }}
        >
          {count} {count === 1 ? t('homepage:homeBeta.familyModal.scenarioSingular', { defaultValue: 'scenario' }) : t('homepage:homeBeta.familyModal.scenarioPlural', { defaultValue: 'scenarios' })}
        </span>
      </div>

      {/* Carousel */}
      <div className="relative">
        <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
          <div className="flex gap-5 px-12">
            {visibleScenarios.map((scenario) => (
              <div
                key={scenario.id}
                className="flex-shrink-0"
                style={{ width: cardWidth }}
              >
                {renderCard(scenario)}
              </div>
            ))}
          </div>
        </div>

        {/* Left arrow */}
        {canScrollPrev && (
          <button
            onClick={() => emblaApi?.scrollPrev()}
            aria-label={t('homepage:homeBeta.browse.scrollPrev', { defaultValue: 'Scroll left' })}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: arrowBg,
              border: `1px solid ${arrowBorder}`,
              boxShadow: arrowShadow,
              color: arrowColor,
            }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/* Right arrow */}
        {canScrollNext && (
          <button
            onClick={() => emblaApi?.scrollNext()}
            aria-label={t('homepage:homeBeta.browse.scrollNext', { defaultValue: 'Scroll right' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: arrowBg,
              border: `1px solid ${arrowBorder}`,
              boxShadow: arrowShadow,
              color: arrowColor,
            }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
